import React, { useEffect, useState } from 'react';
import { authFetch } from '@/lib/auth';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { CalendarDays, Apple, Mail, KeyRound } from 'lucide-react';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

// scheduled_start is naive Arizona wall-clock time; format without TZ conversion
const parseLocal = (iso) => {
  const [d, t = '00:00'] = iso.split('T');
  const [y, m, day] = d.split('-').map(Number);
  const [h, mi] = t.split(':').map(Number);
  return new Date(y, m - 1, day, h, mi);
};

export const formatShootWindow = (startIso, endIso) => {
  const s = parseLocal(startIso);
  const e = parseLocal(endIso);
  const date = s.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
  const t = (x) => x.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  return { date, time: `${t(s)} – ${t(e)}` };
};

// Rewrites the ICS link to this environment's backend so it works in preview too
const localize = (url) => {
  try {
    const u = new URL(url);
    return `${API_BASE}${u.pathname}${u.search}`;
  } catch {
    return url;
  }
};

export const useCalendarLinks = (orderId, refreshKey) => {
  const [links, setLinks] = useState(null);
  useEffect(() => {
    if (!orderId) return;
    authFetch(`${API_BASE}/api/scheduling/orders/${orderId}/calendar-links`)
      .then((r) => (r.ok ? r.json() : null))
      .then(setLinks)
      .catch(() => setLinks(null));
  }, [orderId, refreshKey]);
  return links;
};

export const CalendarButtons = ({ links, prefix, showAdmin = false }) => (
  <div className="flex flex-wrap gap-2">
    <Button asChild size="sm" className="bg-[#C6A14A] text-[#050816] hover:bg-[#d4b25c]">
      <a href={localize(links.ics_url)} data-testid={`${prefix}-apple-btn`}>
        <Apple className="h-4 w-4 mr-1.5" /> Apple Calendar
      </a>
    </Button>
    <Button asChild size="sm" variant="outline" className="border-white/15 bg-transparent text-white hover:bg-white/5">
      <a href={localize(links.ics_url)} data-testid={`${prefix}-outlook-btn`}>
        <Mail className="h-4 w-4 mr-1.5" /> Outlook
      </a>
    </Button>
    <Button asChild size="sm" variant="outline" className="border-white/15 bg-transparent text-white hover:bg-white/5">
      <a href={links.google_url} target="_blank" rel="noopener noreferrer" data-testid={`${prefix}-google-btn`}>
        <CalendarDays className="h-4 w-4 mr-1.5" /> Google Calendar
      </a>
    </Button>
    {showAdmin && links.admin_ics_url && (
      <Button asChild size="sm" variant="outline" className="border-[#C6A14A]/40 bg-transparent text-[#C6A14A] hover:bg-[#C6A14A]/10">
        <a href={localize(links.admin_ics_url)} data-testid={`${prefix}-admin-ics-btn`}>
          <KeyRound className="h-4 w-4 mr-1.5" /> Admin .ics (with lockbox)
        </a>
      </Button>
    )}
  </div>
);

export const ScheduledShootCard = ({ links, prefix = 'calendar', showAdmin = false, actions = null }) => {
  if (!links?.scheduled) return null;
  const { date, time } = formatShootWindow(links.scheduled_start, links.scheduled_end);
  return (
    <Card className="bg-[#0f1724] border-[#C6A14A]/30" data-testid={`${prefix}-card`}>
      <CardHeader className="pb-3">
        <CardTitle className="text-lg text-white flex items-center gap-2">
          <CalendarDays className="h-5 w-5 text-[#C6A14A]" /> Scheduled Shoot
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div>
          <p className="text-white text-lg" data-testid={`${prefix}-date`}>{date}</p>
          <p className="text-[#E8E4DE]/70 text-sm" data-testid={`${prefix}-time`}>{time} <span className="text-[#E8E4DE]/40">(Arizona time)</span></p>
        </div>
        <div>
          <p className="text-xs uppercase tracking-wider text-[#E8E4DE]/50 mb-2">Add to my calendar</p>
          <CalendarButtons links={links} prefix={prefix} showAdmin={showAdmin} />
        </div>
        {actions}
      </CardContent>
    </Card>
  );
};
